import { PaymentMethod } from "./Gig";

type InconvenientTime = {
  time: string;
  rate: number;
  percentage: boolean;
  amount: number;
};

type AbsenceDetail = {
  time: string;
  dates: string[];
};

type Absence = {
  sickLeave: AbsenceDetail;
  sickLeaveChild: AbsenceDetail;
  vacation: AbsenceDetail;
  leaveOfAbsence: AbsenceDetail;
  parentalLeave: AbsenceDetail;
  other: AbsenceDetail;
};

export type Salary = {
  workerId: number;
  firstName: string;
  lastName: string;
  personalIdentityNumber: string;
  totalWorkTime: string;
  totalInconvenientTime: string;
  totalAbsenceTime: string;
  totalGrossSalary: number;
  grossSalary: number;
  inconvenientTimeSalary: number;
  socialFee: number;
  inconvenientTime: InconvenientTime[];
  absence: Absence;
};

export type SalaryNotice = {
  additionalRows?: WorkerSalaryNoticeAdditionalRowResponse[];
  id: number;
  workerId: number;
  workerName: string;
  companyId: number;
  companyName: string;
  period: string;
  grossSalary: number;
  preliminaryTax: number;
  netSalary: number;
  workedHours: number;
  taxPercentage: number;
  currency: string;
  payrollTax: number;
  payrollType: string;
  deductionAmount: number;
  taxTable: number;
};

export type AGIAbsence = {
  date: string;
  type: number;
  hours: number;
};

export type PayoutDetails = {
  description: string;
  period: string;
  grossPayoutAmount: number;
  employerTaxAmount: number;
  workerTaxAmount: number;
};

export interface SalaryPayoutWorker {
  readonly workerId: number;
  readonly fullName: string;
}

export interface SalaryPayoutPreviewWorker extends SalaryPayoutWorker {
  readonly payoutAmount: number;
}

export interface SalaryPayoutPreviewResponse {
  readonly noPaymentMethodWorkers: readonly SalaryPayoutWorker[];
  readonly noPayoutWorkers: readonly SalaryPayoutWorker[];
  readonly payoutWorkers: readonly SalaryPayoutPreviewWorker[];
}

export interface CreateSalaryPayoutFileRequest {
  readonly payoutDate: string;
  readonly workerIds: readonly number[];
}

export type workersAGI = {
  workerId: number;
  firstName: string;
  lastName: string;
  externalId: string;
  personalIdentityNumber: string;
  period: string;
  totalGrossPayoutAmount: number;
  totalEmployerTax: number;
  totalWorkerTax: number;
  payoutDetails: PayoutDetails[];
  absences: AGIAbsence[];
};

export enum PayoutType {
  Monthly = "Monthly",
  SimflexityPayMonthly = "SimflexityPayMonthly",
  SimflexityPayAdvance = "SimflexityPayAdvance",
  MonthlyPerHour = "MonthlyPerHour",
}

export enum AbsenceType {
  PARENTAL_LEAVE = 9,
  VAB = 7,
}

export type MonthWorkingHours = {
  period: string;
  hours: number;
};

export type FutureOrPastSalary = {
  amount: number;
  workPercentage: number;
  startDate: string;
};

export type MonthSalary = {
  id: number;
  companyId: number;
  workerId: number;
  firstName: string;
  lastName: string;
  externalId: string;
  amount: number;
  workPercentage: number;
  startDate: string;
  futureSalary: FutureOrPastSalary;
  pastSalaries: FutureOrPastSalary[];
  isActive: boolean;
  endDate: string;
};

export type UpdateSalary = {
  companyId: number;
  workerId: number;
  amount: number;
  workPercentage: number;
  startDate: string;
};

export enum MonthSalariesActions {
  GetAllSalaries = "GET_ALL_SALARIES",
  UpdateWorkerSalary = "UPDATE_WORKER_SALARY",
  ClearSalaries = "CLEAR_SALARIES",
}

export enum SalaryType {
  Hourly = "Hourly",
  Monthly = "Monthly",
}

export type AddAdditionalRowRequest = {
  count?: number;
  key: number;
  period?: string;
  salaryNoticeId?: number;
  value: number;
};

export type WorkerSalaryNoticeRowConfig = {
  companyId?: number;
  id: number;
  includeInPayout: boolean;
  key: number;
  showCount: boolean;
  showPerUnit: boolean;
  taxBasis: string;
  text?: string;
  unit?: string;
  valueBasis: string;
};

export type WorkerSalaryNoticeAdditionalRowResponse = {
  companyId: number;
  count: number;
  id: number;
  key?: number;
  period?: string;
  rowConfig?: WorkerSalaryNoticeRowConfig;
  rowConfigId: number;
  value: number;
  workerId: number;
};

export type SalaryFormData = {
  employmentType: WorkerEmploymentType | undefined;
  salaryType: WorkerSalaryType | undefined;
  salary: number;
  startDate: string;
  endDate: string | null;
  workPercentage: number;
  taxTable: number;
  paymentType: PaymentMethod;
  role: string;
  employeeCategory: EmployeeCategory | undefined;
  employeeNumber: number;
};

export type CompanyWorkerSalaryEmployment = {
  id: number;
  startDate: string;
  endDate: string;
  employmentType: WorkerEmploymentType;
  employeeNumber: number;
  employeeCategory: EmployeeCategory;
  salaryType: WorkerSalaryType;
  workPercentage: number;
  paymentType: PaymentMethod;
  taxType: string;
  taxValue: number;
  salary: number;
};

export type CompanyWorkerSalary = {
  workerId: number;
  firstName: string | null;
  lastName: string | null;
  isActive: boolean;
  isRegistered: boolean;
  externalId: string | null;
  endDate: string | null;
  employment: CompanyWorkerSalaryEmployment;
};

/* --- v3 worker / employment / salary / tax shapes --- */

export enum WorkerEmploymentType {
  UNDEFINED = "Undefined",
  EMPLOYEE = "Employee",
  TEMPORARY_WORKER = "TemporaryWorker",
  EXCLUSIVE_TEMPORARY_WORKER = "ExclusiveTemporaryWorker",
  CONSULTANT = "Consultant",
  FREELANCER = "Freelancer",
  SIMFLEXITY_EMPLOYEE = "SimflexityEmployee",
  PROJECT_EMPLOYEE = "ProjectEmployee",
  TRAINEE = "Trainee",
}

export enum EmployeeCategory {
  UNDEFINED = "Undefined",
  OWNER = "Owner",
  BLUE_COLLAR = "BlueCollar",
  WHITE_COLLAR = "WhiteCollar",
}

export enum WorkerSalaryType {
  UNDEFINED = "Undefined",
  HOURLY = "Hourly",
  MONTHLY = "Monthly",
}

export enum CompanyEmploymentTaxType {
  UNDEFINED = "Undefined",
  TABLE = "Table",
  PERCENTAGE = "Percentage",
}

export type CompanyWorkerEmploymentSalaryV3Response = {
  id: number;
  startDate: string;
  value: number;
  isActive: boolean;
};

export type CompanyWorkerEmploymentV3Response = {
  id: number;
  startDate: string;
  endDate: string;
  employmentType: WorkerEmploymentType;
  employeeNumber: number;
  employeeCategory: EmployeeCategory;
  salaryType: WorkerSalaryType;
  workPercentage: number;
  paymentType: PaymentMethod;
  role: string | null;
  agreement: string | null;
  vacationDays: number;
  noticeDays: number;
  workplace: string | null;
  probationPeriodEndDate: string | null;
  isActive: boolean;
  salaries: CompanyWorkerEmploymentSalaryV3Response[];
};

export type CompanyWorkerTaxV3Response = {
  id: number;
  startDate: string;
  type: CompanyEmploymentTaxType;
  value: number;
  isActive: boolean;
};

export type CompanyWorkerInfoV3Response = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  personalIdentityNumber: string | null;
  externalId: string | null;
  address: string | null;
  postalCode: string | null;
  city: string | null;
  countryId: number | null;
  description: string | null;
  location: string | null;
  skills: unknown[] | null;
  stats: unknown[] | null;
};

export type CompanyWorkerV3Response = {
  companyId: number;
  workerId: number;
  isActive: boolean;
  isRegistered: boolean;
  isDeletable: boolean;
  worker: CompanyWorkerInfoV3Response;
  employments: CompanyWorkerEmploymentV3Response[] | null;
  taxes: CompanyWorkerTaxV3Response[] | null;
  workGroups: unknown[] | null;
};

export type UpdateEmploymentV3Request = {
  startDate: string;
  endDate: string | null;
  employmentType: WorkerEmploymentType;
  employeeNumber: number;
  employeeCategory: EmployeeCategory;
  salaryType: WorkerSalaryType;
  workPercentage: number;
  paymentType: PaymentMethod;
  role: string | null;
  agreement: string | null;
  vacationDays: number;
  noticeDays: number;
  workplace: string | null;
  probationPeriodEndDate: string | null;
};

export type UpdateEmploymentSalaryV3Request = {
  startDate: string;
  value: number;
};

export type UpdateWorkerTaxV3Request = {
  startDate: string;
  taxType: CompanyEmploymentTaxType;
  value: number;
};
